import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { ChevronRightIcon, UsersIcon, CheckIcon } from 'lucide-react';
import { Container, Section, SectionHeader, Kicker } from '../components/ui/Primitives';
import { Button } from '../components/ui/Button';
import { Seo } from '../components/Seo';
import { SessionTime, sessionDateLabel } from '../components/webinars/SessionTime';
import { RegistrationForm } from '../components/webinars/RegistrationForm';
import { getWebinar, webinarStatus, webinarStart, webinarEnd, upcomingWebinars, SESSION_TIME } from '../data/webinars';
import { breadcrumbSchema, canonical, SITE } from '../lib/seo';
import { NotFound } from './NotFound';
export function WebinarSession() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const webinar = getWebinar(slug ?? '');
  const [replayUrl, setReplayUrl] = React.useState<string | null>(null);
  if (!webinar) return <NotFound data-id="element-1715" data-mp="x9l53gp7dyso" />;
  const past = webinarStatus(webinar) === 'past';
  const others = upcomingWebinars().filter(item => item.slug !== webinar.slug).slice(0, 3);
  const schema = [{
    '@type': 'Event',
    '@id': `${canonical(`/webinars/${webinar.slug}`)}#event`,
    name: webinar.title,
    description: webinar.description,
    startDate: webinarStart(webinar).toISOString(),
    endDate: webinarEnd(webinar).toISOString(),
    eventAttendanceMode: 'https://schema.org/OnlineEventAttendanceMode',
    eventStatus: 'https://schema.org/EventScheduled',
    inLanguage: 'en-GB',
    url: canonical(`/webinars/${webinar.slug}`),
    location: {
      '@type': 'VirtualLocation',
      url: canonical(`/webinars/${webinar.slug}`)
    },
    organizer: {
      '@id': `${SITE.url}/#organisation`
    },
    performer: webinar.speakers.map(speaker => ({
      '@type': 'Person',
      name: speaker.name,
      jobTitle: speaker.role
    })),
    audience: {
      '@type': 'Audience',
      audienceType: webinar.audience
    },
    isAccessibleForFree: true,
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'GBP',
      availability: 'https://schema.org/InStock',
      url: canonical(`/webinars/${webinar.slug}`),
      validFrom: '2026-09-01'
    },
    superEvent: {
      '@id': `${canonical('/webinars')}#series`
    }
  }, breadcrumbSchema([{
    name: 'Home',
    path: '/'
  }, {
    name: 'Webinars',
    path: '/webinars'
  }, {
    name: webinar.title,
    path: `/webinars/${webinar.slug}`
  }])];
  return <>
      <Seo title={webinar.title} description={`${webinar.audience}. ${sessionDateLabel(webinar)}, ${SESSION_TIME.gmt}, sixty minutes, free to attend.`} path={`/webinars/${webinar.slug}`} jsonLd={schema} data-id="element-1716" data-mp="23dq5qajd8dj" />

      {/* Hero */}
      <section className="surface-dark" data-id="element-1717" data-mp="2iqok7do0fkcb">
        <Container className="py-12 sm:py-16" data-id="element-1718" data-mp="2irpu3etai0sg">
          <nav aria-label="Breadcrumb" className="mb-8" data-id="element-1719" data-mp="2drv4del93h8z">
            <ol className="flex flex-wrap items-center gap-2 text-small text-white/55" data-id="element-1720" data-mp="2dqaalr6rg2c6">
              <li data-id="element-1721" data-mp="tb42nfx5e8an">
                <Link to="/" className="hover:text-white" data-id="element-1722" data-mp="tbnmx3a9t8ru">
                  Home
                </Link>
              </li>
              <li aria-hidden="true" data-id="element-1723" data-mp="y6jczxvy5m2b">
                <ChevronRightIcon className="h-3.5 w-3.5" data-id="element-1724" data-mp="12a8ygehbrnj4" />
              </li>
              <li data-id="element-1725" data-mp="170z843wsacjm">
                <Link to="/webinars" className="hover:text-white" data-id="element-1726" data-mp="171h91due5exv">
                  Webinars
                </Link>
              </li>
              <li aria-hidden="true" data-id="element-1727" data-mp="1c02crn8uqgr2">
                <ChevronRightIcon className="h-3.5 w-3.5" data-id="element-1728" data-mp="1gz564afgyyj7" />
              </li>
              <li aria-current="page" className="truncate text-white/80" data-id="element-1729" data-mp="1h185vm6fc614">
                {webinar.month}
              </li>
            </ol>
          </nav>

          <Kicker data-id="element-1730" data-mp="1pys9h1rreeew">{past ? 'Recorded session' : `${webinar.month} session`}</Kicker>
          <h1 className="mt-4 max-w-4xl font-display text-[30px] font-light leading-tight text-white sm:text-[44px]" data-id="element-1731" data-mp="3nqmtuw5tid8n">
            {webinar.title}
          </h1>
          <SessionTime webinar={webinar} reversed className="mt-7" data-id="element-1732" data-mp="3rt9mgzabc26w" />
        </Container>
      </section>

      {/* Body and form */}
      <Container className="py-12 sm:py-16" data-id="element-1733" data-mp="2sg7xausbv3sx">
        <div className="grid gap-12 lg:grid-cols-[1fr_0.95fr] lg:gap-16" data-id="element-1734" data-mp="2sfexyevp7gkt">
          <div data-id="element-1735" data-mp="36e4thq2vd872">
            {/* The description stays in the served markup, gate or no gate. */}
            <h2 className="font-display text-[24px] leading-snug text-ink" data-id="element-1736" data-mp="31ho4yurrcrtg">About this session</h2>
            <p className="mt-5 max-w-prose text-[17px] leading-relaxed text-body" data-id="element-1737" data-mp="2drk24pme8w3i">{webinar.description}</p>

            <div className="mt-10 grid gap-8 border-t border-line pt-8 sm:grid-cols-2" data-id="element-1738" data-mp="28t0hcsjjdqcq">
              <div data-id="element-1739" data-mp="2nhn1d7k3z6aa">
                <h3 className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted" data-id="element-1740" data-mp="2nhwkcb6qkd6h">
                  Who it is for
                </h3>
                <p className="mt-3 text-[16px] text-ink" data-id="element-1741" data-mp="1uzr7kqrof2to">{webinar.audience}</p>
              </div>
              <div data-id="element-1742" data-mp="1q1ppo95zl9js">
                <h3 className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted" data-id="element-1743" data-mp="1q06aymzzb6k4">Focus</h3>
                <p className="mt-3 text-[16px] text-ink" data-id="element-1744" data-mp="5idkjh1xtcll">{webinar.focus}</p>
              </div>
            </div>

            <div className="mt-8 border-t border-line pt-8" data-id="element-1745" data-mp="abgf9sigas3d">
              <h3 className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted" data-id="element-1746" data-mp="3soej4aid652t">Speakers</h3>
              <ul className="mt-3 space-y-2" data-id="element-1747" data-mp="jamigkdk6n4">
                {webinar.speakers.map(speaker => <li key={speaker.name} className="flex items-start gap-2.5 text-[16px] text-body" data-id="element-1748" data-mp="oapwmvie7s8x">
                    <UsersIcon className="mt-1 h-4 w-4 shrink-0 text-gold" aria-hidden="true" data-id="element-1749" data-mp="t7oqdcy59x6l" />
                    <span data-id="element-1750" data-mp="f7x3sntkb8gz">
                      <span className="text-ink" data-id="element-1751" data-mp="f8p060uzha8b">{speaker.name}</span>, {speaker.role}
                    </span>
                  </li>)}
              </ul>
            </div>

            <div className="mt-8 border-t border-line pt-8" data-id="element-1752" data-mp="2zyeac0kqs7uo">
              <h3 className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted" data-id="element-1753" data-mp="34ytq0yshxrft">
                What registrants receive
              </h3>
              <ul className="mt-3 space-y-2.5 text-[16px] text-body" data-id="element-1754" data-mp="1ahajwxajzn19">
                {['A calendar invitation and the joining link, by email', 'Reminders one week, one day and one hour before', 'The recording and a one page summary afterwards, whether or not you attend'].map(item => <li key={item} className="flex gap-3" data-id="element-1755" data-mp="rslx25sif7mu">
                    <CheckIcon className="mt-1 h-4 w-4 shrink-0 text-gold" aria-hidden="true" data-id="element-1756" data-mp="mshi4k792kyq" />
                    {item}
                  </li>)}
              </ul>
            </div>
          </div>

          <div className="lg:sticky lg:top-28 lg:self-start" data-id="element-1757" data-mp="ds2w1imgp1u6">
            {past && replayUrl && <figure className="mb-6 overflow-hidden rounded-md border border-line bg-panel" data-id="element-1758" data-mp="2c7xoxsxpbulg">
                <video controls autoPlay preload="metadata" playsInline className="block aspect-video w-full bg-charcoal" data-id="element-1759" data-mp="2863u41a6tkt7">
                  <source src={replayUrl} type="video/mp4" data-id="element-1760" data-mp="3q2e7gdu2y581" />
                  Your browser cannot play embedded video.
                </video>
                <figcaption className="px-5 py-4 text-small text-muted" data-id="element-1761" data-mp="3v0fnepw7lofa">
                  Recorded {sessionDateLabel(webinar)}. This link is signed for you and expires after fifteen
                  minutes.
                </figcaption>
              </figure>}

            {!replayUrl && <RegistrationForm webinar={webinar} mode={past ? 'replay' : 'register'} onReplayReady={setReplayUrl} data-id="element-1762" data-mp="376xvufb3ti0d" />}
          </div>
        </div>
      </Container>

      {/* Other sessions */}
      {others.length > 0 && <Section tone="mist" aria-labelledby="other-sessions" data-id="element-1763" data-mp="3l1zxurctyn7w">
          <SectionHeader id="other-sessions" kicker="Also coming up" title="Other sessions this year." data-id="element-1764" data-mp="ve93229zzi7y" />
          <div className="mt-10 grid gap-8 sm:grid-cols-3" data-id="element-1765" data-mp="10ckbc8kn9ko2">
            {others.map(item => <article key={item.slug} data-id="element-1766" data-mp="c2342fw0zouu">
                <p className="text-small uppercase tracking-[0.12em] text-gold" data-id="element-1767" data-mp="72gvijr56wsd">{item.month}</p>
                <h3 className="mt-3 font-display text-[19px] leading-snug text-ink" data-id="element-1768" data-mp="ktvdqod86tyd">
                  <Link to={`/webinars/${item.slug}`} className="underline-offset-4 hover:underline" data-id="element-1769" data-mp="kvpz23seso2z">
                    {item.title}
                  </Link>
                </h3>
                <p className="mt-3 text-[15px] text-body" data-id="element-1770" data-mp="uq853flasvpy">{item.audience}</p>
              </article>)}
          </div>
        </Section>}
    </>;
}